import { Box } from "@chakra-ui/layout";
import { Col, Row } from "antd";
import useDeleteMensagem from "features/mensagens/hooks/useDeleteMensagem";
import useArquivarMensagem from "features/mensagens/hooks/useArquivarMensagem";
import useMensagens from "features/mensagens/hooks/useMensagens";
import { MensagemOrigem } from "features/mensagens/typings";
import {
  showConfirm2,
  showErrorAxios,
  showSuccess,
} from "infra/helpers/alerta";
import MenuDetalhes from "components/Menu/MenuDetalhes";
import PageHeader from "components/Page/PageHeader";
import { useMensagemModal } from "components/DrawerNovaMensagem/MensagemModalContext";
import useFiltroMensagens from "../../hooks/useFiltroMensagens";
import MensagensTable from "../MensagensTable";
import InfiniteScroll from "react-infinite-scroll-component";
import { useMemo, useState } from "react";
import useHasPermission from "features/permissions/hooks/useHasPermission";
import { PermissaoEnum } from "resources/enums/permissao";
import {
  IoMdMailOpen,
  IoIosSend,
  IoIosTrash,
  IoIosArchive,
} from "react-icons/io";
import { useTotalMensagemNaoRespondida } from "features/mensagens/hooks/useTotalMensagemNaoRespondida";
import InfiniteScrollLoader from "components/InfiniteScrollLoader";
import { TipoDestinoEnum } from "features/documentos/enums";
import { useLocation } from "react-router";
import { useLoading } from "ui/components/GlobalLoading";
import { MensagemItem } from "core/features/mensagens/typings";
import { useRegrasTenant } from "core/features/tenants/tenants.context";
import ModalInicioSubtenantsView from "components/ModalInicioSubtenantsView";
import ModalFiltroMensagens from "../MensagensTable/components/ModalFiltroMensagens";

function ListaMensagens() {
  const { search } = useLocation();
  const params = new URLSearchParams(search);
  const tipoDestinoId = params.get("tipoDestinoId") as TipoDestinoEnum;

  const { useSubtenantsViews } = useRegrasTenant();

  const [isOpenModalInicioSubtenantsView, setIsOpenModalInicioSubtenantsView] =
    useState(useSubtenantsViews);

  const [isOpenModalFiltros, setIsOpenModalFiltros] = useState<boolean>(false);
  const [activeListQuery, setActiveListQuery] = useState<boolean>(
    !useSubtenantsViews
  );

  const { onOpen: openDrawer } = useMensagemModal();
  const { filtro, onChangeFiltro } = useFiltroMensagens({ tipoDestinoId });
  const {
    data: mensagens,
    isLoading,
    isRefetching,
    refetch,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage,
  } = useMensagens({ ...filtro }, activeListQuery);

  const { data: total, isLoading: isLoadingTotal } =
    useTotalMensagemNaoRespondida(
      {
        tenantsIds: filtro.tenantsIds,
      },
      activeListQuery
    );

  const { mutate: handleDelete, isLoading: isLoadingDelete } =
    useDeleteMensagem();

  const { mutate: handleArquivar, isLoading: isLoadingArquivar } =
    useArquivarMensagem();

  const items = useMemo(() => {
    return (
      mensagens?.pages.reduce(
        (agg: MensagemItem[], item) => agg.concat(item.items ?? []),
        []
      ) ?? []
    );
  }, [mensagens]);

  const onChangeMenu = (origem: MensagemOrigem) => {
    origem === "recebida"
      ? onChangeFiltro({
          origem,
          orderBy: undefined,
          tipoDestinoId: undefined,
          orderDirection: undefined,
        })
      : onChangeFiltro({
          origem,
        });
  };

  const onChangeOrder = (
    changeValue: "orderBy" | "orderDirection",
    value?: "ASC" | "DESC" | string
  ) => onChangeFiltro({ [changeValue]: value });
  const hasPermissaoMensagens = useHasPermission(
    PermissaoEnum.Backoffice.Administrativo.Mensagem.Visualizar
  );

  useLoading(isLoadingDelete || isLoadingArquivar || isRefetching);
  return (
    <>
      <InfiniteScroll
        dataLength={items.length}
        next={() => {
          fetchNextPage();
        }}
        style={{
          overflowX: "hidden",
        }}
        scrollThreshold={0.95}
        hasMore={!!hasNextPage}
        loader={isFetchingNextPage ? <InfiniteScrollLoader /> : null}
      >
        <PageHeader
          title="Mensagens"
          titleButton={!useSubtenantsViews ? "Nova mensagem" : undefined}
          onClick={!useSubtenantsViews ? () => openDrawer(refetch) : undefined}
          permissions={PermissaoEnum.Backoffice.Administrativo.Mensagem.Criar}
          mb="0px"
        />

        <Box>
          <Box as={Row} mt="32px" gutter={16} position="relative">
            {hasPermissaoMensagens ? (
              <Box as={Col} xs={4}>
                <Box position="sticky" top={"76px"}>
                  <MenuDetalhes<MensagemOrigem>
                    options={[
                      {
                        label: "Recebidas",
                        route: MensagemOrigem.Recebida,
                        permissions: [
                          PermissaoEnum.Backoffice.Administrativo.Mensagem
                            .Visualizar,
                        ],
                        count: total?.total,
                        loadingCount: isLoadingTotal,
                        icon: <IoMdMailOpen />,
                      },
                      {
                        label: "Enviadas",
                        route: MensagemOrigem.Enviada,
                        permissions: [
                          PermissaoEnum.Backoffice.Administrativo.Mensagem
                            .Visualizar,
                        ],
                        icon: <IoIosSend />,
                      },
                      {
                        label: "Arquivadas",
                        route: MensagemOrigem.Arquivada,
                        permissions: [
                          PermissaoEnum.Backoffice.Administrativo.Mensagem
                            .Visualizar,
                        ],
                        icon: <IoIosArchive />,
                      },
                      {
                        label: "Excluídas",
                        route: MensagemOrigem.Excluida,
                        permissions: [
                          PermissaoEnum.Backoffice.Administrativo.Mensagem
                            .Visualizar,
                        ],
                        icon: <IoIosTrash />,
                      },
                    ]}
                    selectedOption={filtro.origem}
                    onChangeOption={onChangeMenu}
                  />
                </Box>
              </Box>
            ) : null}
            <Box as={Col} xs={hasPermissaoMensagens ? 20 : 24} minH={"500px"}>
              <MensagensTable
                destino={filtro.tipoDestinoId}
                isPortaria={filtro.tipoDestinoId === TipoDestinoEnum.Portaria}
                onSearchPortaria={(isBool) =>
                  onChangeFiltro({
                    tipoDestinoId: isBool
                      ? TipoDestinoEnum.Portaria
                      : undefined,
                  })
                }
                onChangeOrder={onChangeOrder}
                data={items ?? []}
                isLoading={isLoading}
                searchValue={filtro.search || ""}
                onSearch={(search) => onChangeFiltro({ search })}
                setIsOpenModalFiltros={setIsOpenModalFiltros}
                date={{
                  dataInicio: filtro?.dataInicio,
                  dataFim: filtro.dataFim,
                }}
                mensagensRespondidas={filtro.mensagensRespondidas}
                onSelectMensagensRespondidas={(t) =>
                  onChangeFiltro({
                    mensagensRespondidas: t,
                  })
                }
                origem={filtro.origem}
                filtro={filtro}
                onDelete={(id) =>
                  showConfirm2({
                    onOk: () => {
                      handleDelete(id, {
                        onSuccess: async () => {
                          await showSuccess();
                          refetch();
                        },
                        onError: (err) => {
                          showErrorAxios(err);
                        },
                      });
                    },
                  })
                }
                onArquivar={(id) =>
                  showConfirm2({
                    onOk: () => {
                      handleArquivar(id, {
                        onSuccess: async () => {
                          await showSuccess();
                          refetch();
                        },
                        onError: (err) => {
                          showErrorAxios(err);
                        },
                      });
                    },
                  })
                }
              />
            </Box>
          </Box>
        </Box>
      </InfiniteScroll>

      <ModalFiltroMensagens
        showFilter={filtro.origem === MensagemOrigem.Recebida}
        onChangeFiltro={(filtro) => {
          onChangeFiltro(filtro);
          setIsOpenModalFiltros(false);
          setActiveListQuery(true);
        }}
        onClose={() => setIsOpenModalFiltros(false)}
        open={isOpenModalFiltros}
        filtro={filtro}
      />

      {useSubtenantsViews && isOpenModalInicioSubtenantsView ? (
        <ModalInicioSubtenantsView
          isOpen={isOpenModalInicioSubtenantsView}
          onClose={() => setIsOpenModalInicioSubtenantsView(false)}
          onOpenFiltros={() => {
            setIsOpenModalFiltros(true);
            setIsOpenModalInicioSubtenantsView(false);
          }}
          onVerTudo={() => {
            setActiveListQuery(true);
            setIsOpenModalInicioSubtenantsView(false);
          }}
        />
      ) : null}
    </>
  );
}

export default ListaMensagens;
